import { Card, CardContent } from '@mlm/ui';
import type { KpiCardData } from '../../lib/api';
import { formatPeso } from './format';
import { TenantWideBadge } from './tenant-wide-badge';

/**
 * Lifetime totals — the counterpart to the "This month" flows. These INCLUDE
 * imported history: sales sums every completed order (incl. inert legacy_import
 * rows) and earnings sums all cash credited to members (incl. imported per-plan
 * earning totals). Earnings is labelled "recorded" because it's only as complete
 * as each tenant's imported breakdown data — a snapshot, not a dated trend.
 */
export function AllTimeSummary({
  sales,
  earnings,
  filtered = false,
}: {
  sales?: KpiCardData;
  earnings?: KpiCardData;
  filtered?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <p className="text-caption uppercase tracking-[0.1em] font-medium text-muted-foreground">
          All-time
        </p>

        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Figure
            label="Total sales"
            value={sales ? formatPeso(sales.value) : '0.00'}
            badge={<TenantWideBadge show={filtered && sales?.scope === 'tenant'} />}
          />
          <div className="sm:border-l sm:border-border sm:pl-6">
            <Figure
              label="Member earnings"
              value={earnings ? formatPeso(earnings.value) : '0.00'}
              sub="recorded"
              badge={<TenantWideBadge show={filtered && earnings?.scope === 'tenant'} />}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Figure({
  label,
  value,
  sub,
  badge,
}: {
  label: string;
  value: string;
  sub?: string;
  badge?: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <p className="text-caption uppercase tracking-[0.1em] font-medium text-muted-foreground">
          {label}
        </p>
        {sub ? <span className="text-caption text-muted-foreground">· {sub}</span> : null}
        {badge}
      </div>
      <p className="mt-1 text-kpi tabular-nums text-foreground">{value}</p>
    </div>
  );
}
